import React from 'react'
import { useIntl } from 'react-intl'
import { CrmIntakeView } from '../../../features/crm/crmApi'
import { ui } from '../../../utils/uiTokens'
import { InfoBox, InfoGrid, Pill } from './crmUi'
import { amoId, courseTitle, dictLabel, fmtDate, formatPhone, money, subjectsLine } from './format'
import { useCrmDict } from './dict'
import { payStateView } from './stages'

type Row = [string, React.ReactNode]

// Деректер заявки: «Оқушы және ата-ана», «Пәндер және тапсырыс», у рассрочки - отдельный блок
const IntakeInfo: React.FC<{ intake: CrmIntakeView; installmentBox?: boolean }> = ({ intake: x, installmentBox }) => {
    const { formatMessage, locale } = useIntl()
    const dict = useCrmDict()
    const d = x.data
    const lnk = (url?: string | null, label?: string) =>
        url ? (
            <a href={url} target="_blank" rel="noreferrer" style={{ color: ui.pink, fontWeight: 600 }}>
                {label || url}
            </a>
        ) : (
            '-'
        )
    const inst = x.installment || dict.isInstallment(d.payType)
    const st = inst ? payStateView(formatMessage, x) : null

    return (
        <InfoGrid>
            <InfoBox
                title={formatMessage({ id: 'crmStudentAndParent' })}
                rows={[
                    [formatMessage({ id: 'crmStudent' }), d.studentName],
                    [formatMessage({ id: 'crmPhone' }), formatPhone(d.studentPhone)],
                    [formatMessage({ id: 'crmIin' }), d.iin || '-'],
                    [formatMessage({ id: 'crmGrade' }), dictLabel(formatMessage, 'Grade', d.grade)],
                    [formatMessage({ id: 'crmLanguage' }), dictLabel(formatMessage, 'Lang', d.language)],
                    [formatMessage({ id: 'crmParent' }), d.parentName],
                    [formatMessage({ id: 'crmPhone' }), formatPhone(d.parentPhone)],
                ]}
            />
            <InfoBox
                title={formatMessage({ id: 'crmSubjectsAndOrder' })}
                rows={[
                    [
                        formatMessage({ id: 'crmCourse' }),
                        x.courseId
                            ? `${courseTitle(x, locale)} · ${formatMessage({ id: 'crmUntil' }, { date: fmtDate(x.endDate) })}`
                            : formatMessage({ id: 'crmModeratorWillSet' }),
                    ],
                    [formatMessage({ id: 'crmColSubjects' }), subjectsLine(x.subjects, locale)],
                    ['amoCRM', lnk(d.amoUrl, `#${amoId(d.amoUrl)}`)],
                    [formatMessage({ id: 'crmBook' }), `${d.bookOrderNo ? `№${d.bookOrderNo}` : '-'}${d.bookSum ? ` · ${money(d.bookSum)}` : ''}`],
                    [formatMessage({ id: 'crmPackageLabel' }), dict.label('PACKAGE', d.packageCode)],
                    [formatMessage({ id: 'crmPayType' }), dict.label('PAY_TYPE', d.payType)],
                    [formatMessage({ id: 'crmPayMethod' }), `${dict.list('PAY_METHOD', d.payments)} · ${money(d.paidSum)}`],
                    ...(inst
                        ? [
                              [
                                  formatMessage({ id: 'crmNextPay' }),
                                  `${money(x.nextPaySum ?? d.nextPaySum)} · ${
                                      x.accessUntil
                                          ? formatMessage({ id: 'crmUntil' }, { date: fmtDate(x.accessUntil) })
                                          : formatMessage({ id: 'crmModeratorSetsDate' })
                                  }`,
                              ] as Row,
                          ]
                        : []),
                    ...(d.discountSum ? [[formatMessage({ id: 'crmDiscount' }), money(d.discountSum)] as Row] : []),
                    ...(d.quota ? [[formatMessage({ id: 'crmQuota' }), dictLabel(formatMessage, 'Quota', d.quota)] as Row] : []),
                    [formatMessage({ id: 'crmContract' }), lnk(d.contractUrl, formatMessage({ id: 'crmLink' }))],
                    [formatMessage({ id: 'crmSourceShort' }), dict.list('SOURCE', d.sources)],
                    ...(d.note ? [[formatMessage({ id: 'crmNote' }), d.note] as Row] : []),
                ]}
            />
            {installmentBox && st && (
                <InfoBox
                    title={formatMessage({ id: 'crmInstallment' })}
                    rows={[
                        [formatMessage({ id: 'crmInstType' }), dict.label('PAY_TYPE', d.payType)],
                        [formatMessage({ id: 'crmPaidTotal' }), money(x.paidTotal ?? d.paidSum)],
                        [
                            formatMessage({ id: 'crmNextPay' }),
                            `${money(x.nextPaySum ?? d.nextPaySum)}${x.accessUntil ? ` · ${fmtDate(x.accessUntil)}` : ''}`,
                        ],
                        [formatMessage({ id: 'crmState' }), <Pill tone={st.tone}>● {st.text}</Pill>],
                    ]}
                />
            )}
        </InfoGrid>
    )
}

export default IntakeInfo
